import { useState, useEffect, useCallback } from "react";
import _ from "lodash";

import {
  getDocumentArchiveOptions,
  isGrandparentNode
} from "views/documents/state/documentArchiveOptions";
import { ArchiveNodeOption } from "models/ArchiveNodeOption";
import CustomerServiceTreeItem from "./CustomerServiceTreeItem";
import CustomerServiceView from "./CustomerServiceView";
import { useGlobalContext } from "app/store/GlobalContext";
import { useRootStore } from "store";

import "./CustomerServiceTree.scss";

type Props = {
  fpConfig: any;
  onSetFpService: (customerChoice: string) => void;
  onToggleChildServices: (ids: string[], enabled: boolean) => void;
};

export default function CustomerServiceTree({
  fpConfig,
  onSetFpService,
  onToggleChildServices
}: Props) {
  const { setEditingState } = useGlobalContext();

  const { documentServices } = useRootStore((state) => state.wizard.customer);

  const [selectedNode, setSelectedNode] = useState(
    getDocumentArchiveOptions(documentServices)
  );
  const [documentNodes, setDocumentNodes] = useState(
    getDocumentArchiveOptions(documentServices)
  );

    const findNodeById = useCallback((rootNode: any, targetId: string): any => {
    if (rootNode.id === targetId) {
      return rootNode;
    }
    
    if (rootNode.nodes && rootNode.nodes.length > 0) {
      for (const node of rootNode.nodes) {
        const found = findNodeById(node, targetId);
        if (found) {
          return found;
        }
      }
    }
    
    return null;
  }, []);

  const renderTree = useCallback((service: any, flatServices: any) => {
    service.nodes.forEach((node: any) => {
      node.enabled =
        flatServices.filter((fs: any) => fs.id === node.id).length > 0 ||
        node.locked === true;
      if (node.nodes.length !== 0) {
        renderTree(node, flatServices);
      }
    });

    service.enabled =
      service.nodes.length > 0 &&
      service.nodes.every((n: any) => n.enabled === true);
    return service;
  }, []);

  useEffect(() => {
    const updatedDocumentNodes = renderTree(
      { ...getDocumentArchiveOptions(documentServices) },
      documentServices || []
    );
    setDocumentNodes(updatedDocumentNodes);

    // Preserve current selected node if it still exists in the updated tree
    const currentSelectedId = selectedNode?.id;
    if (currentSelectedId && currentSelectedId !== updatedDocumentNodes.id) {
      const foundNode = findNodeById(updatedDocumentNodes, currentSelectedId);
      if (foundNode) {
        setSelectedNode(foundNode);
      } else {
        setSelectedNode(updatedDocumentNodes);
      }
    } else {
      setSelectedNode(updatedDocumentNodes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentServices, renderTree]);

  const findAndSetSelectedNode = useCallback(
    (allNodes: any, serviceId: any) => {
      const firstLevelNodes = allNodes.nodes;
      for (const node of firstLevelNodes) {
        if (node.id === serviceId) {
          setSelectedNode(allNodes);
          return true;
        }
        if (node.nodes && node.nodes.length > 0) {
          if (findAndSetSelectedNode(node, serviceId)) {
            setSelectedNode(node);
            return true;
          }
        }
      }
      return false;
    },
    []
  );

  const handleNodeClick = useCallback(
    (service: any, e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      const shouldClose = isGrandparentNode(service);
      const isSameNode = selectedNode.id === service.id && shouldClose;
      const isNodeExpanded =
        selectedNode.id.startsWith(service.id) && shouldClose;

      if (isSameNode || isNodeExpanded) {
        findAndSetSelectedNode(documentNodes, service.id);
      } else {
        setSelectedNode(service);
      }
    },
    [selectedNode, documentNodes, findAndSetSelectedNode]
  );

  const setService = (service: ArchiveNodeOption) => {
    setEditingState({ isEditing: true, location: "customer-settings" });

    const childless = service.nodes.length === 0;

    if (childless) {
      onToggleChildServices([service.id], !service.enabled);
    } else {
      const serviceIds: string[] = _.flattenDeep(getAllLeafs(service));
      service.enabled = !service.enabled;
      onToggleChildServices(serviceIds, service.enabled);
    }
  };

  const getAllLeafs = (service: any) => {
    return service.nodes.map((serv: any) => {
      return serv.nodes.length === 0 ? serv.id : getAllLeafs(serv);
    });
  };

  return (
    <div className="content">
      {documentServices.length > 0 && (
        <>
          <ul className="wrapper">
            <CustomerServiceTreeItem
              service={documentNodes}
              onNodeClick={handleNodeClick}
              selectedNode={selectedNode}
            />
          </ul>
          <CustomerServiceView
            fpConfig={fpConfig}
            selectedNode={selectedNode}
            onSetService={setService}
            onSetFpService={onSetFpService}
          />
        </>
      )}
    </div>
  );
}
